<template>
  <!-- Its own block after the address, set apart by dividers; the unit wizard's hide-address toggle follows it -->
  <template v-if="destinations.length">
    <Divider />

    <FormField label="Portfolio" :hint="consequence">
      <MenuButton full-width mobile-sheet sheet-title="Portfolio">
        <template #menu-button-trigger>
          <PickerTrigger
            v-if="chosen"
            data-testid="new-unit-portfolio-trigger"
            :label="chosen.name"
            :avatar-name="ownerNameOf(chosen)"
            :detail="ownerWordOf(chosen)"
            selected
          />
          <PickerTrigger
            v-else
            label="No portfolio"
            detail="stays in Unassigned"
            icon="home-03"
            selected
            data-testid="new-unit-portfolio-trigger"
          />
        </template>

        <MenuButtonItem
          title="No portfolio"
          subtitle="Stays in Unassigned"
          icon="home-03"
          :is-selected="!chosen"
          @click="model = ''"
        />

        <MenuButtonItem
          v-for="portfolio in destinations"
          :key="portfolio.id"
          :title="portfolio.name"
          :subtitle="rowSubtitleOf(portfolio)"
          :is-selected="portfolio.id === model"
          @click="model = portfolio.id"
        >
          <template #prefix>
            <Avatar :user-name="ownerNameOf(portfolio)" show-single-letter size="small" />
          </template>
        </MenuButtonItem>
      </MenuButton>
    </FormField>

    <Divider v-if="!last" />
  </template>

  <!-- A member with no portfolio to put it in: said up front, since the unit will sit in none -->
  <template v-else-if="!isAdmin && portfolios.length">
    <Divider />
    <Hint icon="info-circle">
      This listing won't be in a portfolio. An admin, or the owner of a portfolio, can file it later from its Team tab.
    </Hint>
    <Divider v-if="!last" />
  </template>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed, ref, watch } from 'vue';

import { Avatar, Divider, FormField, Hint, MenuButton, MenuButtonItem, PickerTrigger } from '@tallo/design-system';
import {
  PortfolioDto,
  teamMemberName as nameOf,
  useCompanyPortfoliosQuery,
  useCompanyTeamQuery,
} from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';

import { unassignedPortfolioFilter } from '../../utils/portfolio-filter.util';

const props = defineProps<{
  /**
   * What the properties page was showing: a portfolio, chosen for the new unit when the viewer may use
   * it, or `unassigned`, which starts the unit in none
   */
  initial?: string;
  /** Nothing follows the field in its form (the building screen), so the closing divider is left off */
  last?: boolean;
}>();

/** The chosen portfolio's id; empty for none */
const model = defineModel<string>({ required: true });

const { investor, isAdmin } = storeToRefs(useInvestorStore());
const portfoliosQuery = useCompanyPortfoliosQuery();
const { portfolios } = portfoliosQuery;
const { members } = useCompanyTeamQuery();
const hasSettled = ref(false);

// Who may create into a portfolio: an admin, its owner, or a default collaborator, who joins every
// unit filed there anyway. The same rule the server checks, so the list only offers what goes through
const destinations = computed(() =>
  portfolios.value.filter(
    (portfolio) =>
      isAdmin.value ||
      portfolio.ownerId === investor.value?.id ||
      portfolio.collaborators.some(
        (collaborator) => collaborator.investorId === investor.value?.id && collaborator.joinsNewUnits,
      ),
  ),
);

const chosen = computed(() => destinations.value.find((portfolio) => portfolio.id === model.value));

// Whoever creates the unit manages it, in a portfolio or not; a portfolio adds its defaults and, if
// someone else owns it, an owner who runs it
const consequence = computed(() => {
  const portfolio = chosen.value;

  if (!portfolio) {
    return "You'll manage it. It stays in Unassigned, and can be filed into a portfolio later from its Team tab.";
  }

  const owner = ownerOf(portfolio);
  const hasDefaults = portfolio.collaborators.some((collaborator) => collaborator.joinsNewUnits);
  const parts = [
    hasDefaults ? "the portfolio's default collaborators join it" : '',
    owner && !owner.isYou ? `${nameOf(owner)} can reassign or move it` : '',
  ].filter(Boolean);
  const [first, ...rest] = parts;

  return first
    ? `You'll manage it. ${first.charAt(0).toUpperCase()}${first.slice(1)}${rest.map((part) => `, and ${part}`).join('')}.`
    : "You'll manage it.";
});

function ownerOf(portfolio: PortfolioDto) {
  return members.value.find((member) => member.investorId === portfolio.ownerId);
}

function ownerNameOf(portfolio: PortfolioDto): string {
  const owner = ownerOf(portfolio);

  return owner ? nameOf(owner) : portfolio.name;
}

/** After the name on the closed picker: "you", or the owner's first name */
function ownerWordOf(portfolio: PortfolioDto): string {
  const owner = ownerOf(portfolio);

  return !owner ? '' : owner.isYou ? 'you' : nameOf(owner).split(' ')[0];
}

/** "Bob Lee · 3 properties", "Kayla Ortiz (you) · 1 property" */
function rowSubtitleOf(portfolio: PortfolioDto): string {
  const owner = ownerOf(portfolio);
  const who = owner ? (owner.isYou ? `${nameOf(owner)} (you)` : nameOf(owner)) : '';

  return [who, pluralize('property', portfolio.unitCount, true)].filter(Boolean).join(' · ');
}

// Once the list is in: the page's portfolio if the viewer may use it, else a member's only one.
// Later, a chosen portfolio that was deleted meanwhile falls back to none
watch(
  destinations,
  (options) => {
    if (portfoliosQuery.status.value !== 'success') {
      return;
    }

    if (!hasSettled.value) {
      hasSettled.value = true;

      if (props.initial && options.some(({ id }) => id === props.initial)) {
        model.value = props.initial;
      } else if (props.initial !== unassignedPortfolioFilter && !isAdmin.value && options.length === 1) {
        model.value = options[0].id;
      }

      return;
    }

    if (model.value && !options.some(({ id }) => id === model.value)) {
      model.value = '';
    }
  },
  { immediate: true },
);

const isLoading = computed(() => portfoliosQuery.status.value === 'pending');

defineExpose({ isLoading });
</script>
